import { AddCircleOutline, DeleteOutlined, EditOutlined } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { invalidateWashingReportCache, useDeleteWashingReport, washingReportEndpoints } from 'src/api/washing-report'
import NoReports from 'src/assets/svg/no-reports.svg'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { WashingReportDetailsDialog } from 'src/dialogs/WashingReportDetailsDialog'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import { FoulingType, WashingReportDto } from 'src/generated-types/washing-report-dto'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { washingReportQueryKeys } from 'src/query-keys'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'

interface IWashingReport {
  mainComponentId: EntityId
  context?: Context<any> | null
  positionId?: EntityId
  withoutLayout?: boolean
  onAddWashingReport?: (initialValues: WashingReportDto | undefined) => void
  height?: string
  autoHeight?: boolean
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  setDataLength?: (length: number) => void
  disabled?: boolean
}

const WashingReports = ({
  mainComponentId,
  context,
  withoutLayout,
  onAddWashingReport,
  positionId,
  height,
  autoHeight,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  setDataLength,
  disabled,
}: IWashingReport) => {
  const { t } = useTranslation(['equipment', 'common'])
  const { mutateAsync: deleteWashingReport } = useDeleteWashingReport(mainComponentId)
  const theme = useTheme()
  const { showConfirmDialog, showDialog } = useDialogCtx()
  const { customerId, localityId } = useParams()

  const handleViewWashingReport = useCallback(
    (washingReport: WashingReportDto) => {
      showDialog(
        WashingReportDetailsDialog,
        {
          componentProps: {
            washingReport,
          },
          opts: {
            bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
            rootWidth: '1400px',
          },
          sx: {
            '& .MuiPaper-root': {
              width: '1200px',
            },
          },
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )

  const handleDeleteWashingReport = async (id: EntityId) => {
    showConfirmDialog(
      t('delete-washing-report', { ns: 'washing-report' }),
      t('are-you-sure-delete-washing-report', { ns: 'washing-report' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await deleteWashingReport(id, {
          onSuccess: () => {
            toast.success(t('washing-report-deleted-successfully', { ns: 'washing-report' }))
            invalidateWashingReportCache.getWashingReports(mainComponentId)
            invalidationActivityLogCache.getMainComponentActivityLogs({
              mainComponentId: mainComponentId,
            })
            if (positionId) invalidationActivityLogCache.getCageActivityLogs({ positionId: positionId as EntityId })
            if (positionId) invalidationActivityLogCache.getCageMaintenanceLogs({ positionId: positionId as EntityId })
            invalidateLocalityCache.getCageStatus(customerId as EntityId, localityId as EntityId)
            invalidateMainComponentCache.getEquipment(customerId as EntityId)
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
            pop()
          },
          onError: () => {
            toast.error(t('error-while-deleting-washing-report', { ns: 'washing-report' }))
            pop()
          },
        })
      },
    )
  }

  const StaticColumns: GridColumns = useMemo(
    () => [
      {
        field: 'foulingType',
        headerName: t('fouling-type', { ns: 'equipment' }),
        renderCell: params => {
          return t(FoulingType[params.value], { ns: 'equipment' })
        },
        flex: 1,
      },
      {
        field: 'createdBy',
        headerName: t('createdBy', { ns: 'common' }),
        flex: 1.5,
        renderCell: params => {
          return (
            <DynamicTextTypography
              text={getUserDetail(params.row.createdByUser, UserDetail.FullName)}
              fontWeight={500}
              maxLength={25}
            />
          )
        },
      },
      {
        field: 'createdAt',
        headerName: t('createdAt', { ns: 'common' }),
        flex: 1,
        renderCell: params => {
          return params.value && !params.value?.startsWith('0001-01-01')
            ? getFormattedDate(new Date(params.value))
            : '-'
        },
      },
    ],
    [t],
  )

  const washingReportColumns: GridColumns = useMemo(
    () => [
      ...StaticColumns,
      {
        field: 'actions',
        type: 'actions',
        width: 100,
        renderCell: params => {
          return [
            <AuthorizationProvider
              key={'edit'}
              Context={context}
              permissions={[
                permissionValue.Locality.MainComponent.Document.Write,
                permissionValue.ClientAdminPermission,
                permissionValue.ServiceStation.Write,
                permissionValue.ClientAdminPortal.Equipment_Portal,
              ]}
              type={AuthorizationType.Disabled}
            >
              <GridActionButton
                onClick={e => {
                  onAddWashingReport?.(params.row as WashingReportDto)
                  e.stopPropagation()
                }}
                buttonIcon={<EditOutlined sx={{ width: '16px' }} />}
                width="24px"
                data-cy="UpdateWashingReport"
                height="24px"
                shadowWeight={0}
                sx={{ marginRight: '8px' }}
                disabled={disabled}
              />
            </AuthorizationProvider>,
            <AuthorizationProvider
              key={'delete'}
              Context={context}
              permissions={[
                permissionValue.Locality.MainComponent.Document.Write,
                permissionValue.ClientAdminPermission,
                permissionValue.ServiceStation.Write,
                permissionValue.ClientAdminPortal.Equipment_Portal,
              ]}
              type={AuthorizationType.Disabled}
            >
              <GridActionButton
                buttonIcon={<DeleteOutlined sx={{ width: '16px' }} />}
                width="24px"
                data-cy="DeleteWashingReport"
                height="24px"
                shadowWeight={0}
                onClick={() => handleDeleteWashingReport(params.row.id)}
                disabled={disabled}
              />
            </AuthorizationProvider>,
          ]
        },
      },
    ],
    [StaticColumns, context, handleViewWashingReport, onAddWashingReport, disabled],
  )

  const WashingReportsTable = (
    <InfiniteScrollTable<WashingReportDto>
      id={'washing-reports-table'}
      queryUrl={washingReportEndpoints.getWashingReports(mainComponentId)}
      generateQueryKey={(page: number, pageSize: number) =>
        washingReportQueryKeys.GET_WASHING_REPORTS(mainComponentId as EntityId, { page, pageSize })
      }
      pageSize={10}
      onRowClick={params => handleViewWashingReport(params.row as WashingReportDto)}
      columns={washingReportColumns}
      height={height}
      autoHeight={autoHeight}
      maxVisibleRowCount={maxVisibleRowCount}
      getRowHeight={() => 36}
      minRowHeight="36px"
      containerProps={{ sx: { overflowX: 'hidden' } }}
      noDataComponent={() => (
        <Box height="100%">
          <EmptyStatusCard
            imageWidth={'90px'}
            image={NoReports}
            header={t('washing-reports-empty-status-title', { ns: 'equipment' })}
            content={t('washing-reports-empty-status-content', { ns: 'equipment' })}
            imageContainerClasses="pb-4 pt-2"
            contentTopMargin="10px"
            headerFontSize="18px"
            contentFontSize="14px"
            contentClasses="mt-2"
            height="100%"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
      hideDefaultFooterPagination
      spinnerHeight={100}
      setDataLength={setDataLength}
    />
  )

  if (withoutLayout) {
    return WashingReportsTable
  }

  return (
    <CommonPageLayout
      titleSection={{ subtitle: t('washing-reports', { ns: 'equipment' }), subtitleFontSize: '18px' }}
      topAction={[
        {
          buttonText: t('washing-report', { ns: 'equipment' }),
          buttonIcon: <AddCircleOutline />,
          onClick: onAddWashingReport,
          permissionBinaryValues: [
            permissionValue.Locality.MainComponent.Report.Write,
            permissionValue.ClientAdminPermission,
            permissionValue.ServiceStation.Write,
          ],
          variant: 'outlined',
          shadowWeight: null,
          context: context,
        },
      ]}
      titleContainerProps={{ paddingBottom: '5px' }}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        {WashingReportsTable}
      </Box>
    </CommonPageLayout>
  )
}

export default WashingReports
